import { useEffect, useState } from "react";
import { Star, MessageSquareQuote, ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";
import { api, apiError, appReturnUrl } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { Reveal, SectionHeader } from "../components/home/Sections";
import { Stars, TestimonialForm, TestimonialFormInline } from "../components/TestimonialForm";
import { SocialProof } from "../components/home/SocialProof";
import { Button } from "../components/ui/button";

const fmtDate = (d) => new Intl.DateTimeFormat("fr-FR", { dateStyle: "long" }).format(new Date(d));

export default function AvisPage() {
    const { data } = useSite();
    const hero = data?.sections?.avis_hero || {};
    const [featured, setFeatured] = useState(null);
    const [list, setList] = useState(null);
    const [page, setPage] = useState(1);
    const [rating, setRating] = useState(0);
    const [formOpen, setFormOpen] = useState(false);
    const [federated, setFederated] = useState(null); // {review_token, prefill}
    const [fedState, setFedState] = useState(""); // "", "loading", "error"
    const [fedError, setFedError] = useState("");
    const [fromApp, setFromApp] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // Contrat B : lecture du code (query) et du verifier (fragment — jamais envoyé en HTTP),
    // retrait immédiat du fragment de l'historique, échange serveur-à-serveur.
    // Le paramètre src=bodycoachia_app n'est qu'un indice d'affichage (formulaire direct) :
    // il n'accorde AUCUN privilège — l'échange PKCE reste validé côté serveur.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        const src = params.get("src");
        const hash = window.location.hash || "";
        const verifier = new URLSearchParams(hash.replace(/^#/, "")).get("verifier");
        if (src === "bodycoachia_app") setFromApp(true);
        if (!code) return;
        // Retire immédiatement code + fragment de l'URL visible
        window.history.replaceState(null, "", window.location.pathname + (src === "bodycoachia_app" ? "?src=bodycoachia_app" : ""));
        if (src !== "bodycoachia_app" || !verifier) {
            setFedState("error");
            setFedError("Lien incomplet : regénérez-le depuis l'application (Profil → Donner mon avis).");
            return;
        }
        setFedState("loading");
        api
            .post("/federation/review/exchange", { code, code_verifier: verifier })
            .then(({ data: r }) => {
                setFederated({ review_token: r.review_token, prefill: r.prefill });
                setFedState("");
                if (src !== "bodycoachia_app") setFormOpen(true);
            })
            .catch((e) => {
                setFedState("error");
                setFedError(apiError(e, "L'échange sécurisé a échoué."));
            });
    }, []);

    useEffect(() => {
        api.get("/public/avis/featured").then((r) => setFeatured(r.data.items)).catch(() => setFeatured([]));
    }, []);

    useEffect(() => {
        setList(null);
        api.get("/public/avis", { params: { page, limit: 9, rating: rating || undefined } })
            .then((r) => setList(r.data))
            .catch(() => setList({ items: [], total: 0, pages: 0 }));
    }, [page, rating]);

    return (
        <main className="bg-ivory min-h-screen pt-32 pb-24" data-testid="avis-page">
            <div className="container-bc">
                {fedState === "loading" && (
                    <div className="mb-8 rounded-2xl bg-pearl border border-gold/40 p-5 text-center text-ink" data-testid="federation-loading">
                        Vérification sécurisée de votre session application…
                    </div>
                )}
                {fedState === "error" && (
                    <div className="mb-8 rounded-2xl bg-red-50 border border-red-200 p-5 text-center text-red-800" data-testid="federation-error">
                        <p>{fedError}</p>
                        <a
                            href={appReturnUrl("cancelled")}
                            className="inline-flex items-center gap-2 mt-3 bg-forest text-ivory rounded-xl px-5 py-2.5 text-sm font-semibold hover:bg-forest-surface transition-colors"
                            data-testid="federation-error-return"
                        >
                            ← Retourner à l'application BODYCOACHIA
                        </a>
                    </div>
                )}
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <SectionHeader
                        eyebrow={hero.eyebrow || "Témoignages vérifiés par modération"}
                        title={hero.title || "Ils partagent leur expérience BODYCOACHIA"}
                        subtitle={hero.subtitle}
                    />
                    {!fromApp && (
                        <Reveal className="mb-14">
                            <Button onClick={() => setFormOpen(true)} className="bg-forest text-ivory hover:bg-forest-surface gap-2 px-7 py-6 rounded-2xl font-semibold" data-testid="avis-give-button">
                                <MessageSquareQuote size={18} /> {hero.cta_label || "Donner mon avis"}
                            </Button>
                        </Reveal>
                    )}
                </div>

                {fromApp && fedState !== "loading" && fedState !== "error" && (
                    <section className="mb-16 max-w-2xl" data-testid="avis-app-journey">
                        <a
                            href={appReturnUrl(submitted ? "success" : "cancelled")}
                            className="inline-flex items-center gap-2 mb-5 text-forest font-semibold text-sm hover:text-gold-dark transition-colors"
                            data-testid="avis-return-app"
                        >
                            <ArrowLeft size={16} /> Retourner à l'application
                        </a>
                        <TestimonialFormInline
                            prefill={federated?.prefill || null}
                            reviewToken={federated?.review_token || ""}
                            onSubmitted={() => setSubmitted(true)}
                        />
                    </section>
                )}

                {featured && featured.length > 0 && (
                    <section className="mb-16" data-testid="avis-featured">
                        <Reveal>
                            <h2 className="font-heading font-bold text-ink text-xl mb-6 flex items-center gap-2">
                                <Star size={18} className="text-gold-dark fill-gold" /> À la une
                            </h2>
                        </Reveal>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {featured.map((t, i) => (
                                <Reveal key={t.id} delay={i * 0.06}>
                                    <figure className="card-light p-6 h-full border-gold/40" data-testid={`avis-featured-${t.id}`}>
                                        {t.rating != null && <Stars value={t.rating} />}
                                        <blockquote className="mt-3 text-ink text-sm leading-relaxed">« {t.text} »</blockquote>
                                        <figcaption className="mt-4 text-sm font-semibold text-ink-soft">
                                            {t.name} · {fmtDate(t.created_at)}
                                            {t.provenance === "application" && <span className="ml-2 text-[10px] font-mono uppercase tracking-wider bg-forest/10 text-forest px-2 py-0.5 rounded-full">via l'application</span>}
                                        </figcaption>
                                    </figure>
                                </Reveal>
                            ))}
                        </div>
                    </section>
                )}

                <div className="flex gap-2 mb-8 flex-wrap" data-testid="avis-filters">
                    <button onClick={() => { setRating(0); setPage(1); }} className={`px-4 py-1.5 rounded-full text-sm border ${rating === 0 ? "bg-forest text-ivory border-forest" : "border-border text-ink-soft"}`} data-testid="filter-all">
                        Tous
                    </button>
                    {[5, 4, 3, 2, 1].map((r) => (
                        <button key={r} onClick={() => { setRating(r); setPage(1); }} className={`px-4 py-1.5 rounded-full text-sm border flex items-center gap-1 ${rating === r ? "bg-forest text-ivory border-forest" : "border-border text-ink-soft"}`} data-testid={`filter-${r}`}>
                            {r} <Star size={12} className={rating === r ? "fill-ivory" : "fill-gold text-gold"} />
                        </button>
                    ))}
                </div>

                {list === null ? (
                    <p className="text-ink-soft">Chargement…</p>
                ) : list.items.length === 0 ? (
                    <div className="rounded-3xl border-2 border-dashed border-gold/50 bg-pearl p-12 text-center" data-testid="avis-empty">
                        <p className="font-heading font-bold text-ink text-xl">Aucun avis publié dans cette sélection</p>
                        <p className="text-ink-soft mt-2 text-sm">Chaque avis est relu par notre équipe avant publication. Partagez le vôtre !</p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="avis-list">
                            {list.items.map((t) => (
                                <figure key={t.id} className="card-light p-6" data-testid={`avis-${t.id}`}>
                                    {t.rating != null && <Stars value={t.rating} />}
                                    <blockquote className="mt-3 text-ink text-sm leading-relaxed">« {t.text} »</blockquote>
                                    <figcaption className="mt-4 text-sm font-semibold text-ink-soft">{t.name} · {fmtDate(t.created_at)}</figcaption>
                                </figure>
                            ))}
                        </div>
                        {list.pages > 1 && (
                            <div className="flex items-center justify-center gap-4 mt-10" data-testid="avis-pagination">
                                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="avis-prev"><ChevronLeft size={15} /> Précédent</Button>
                                <span className="text-sm text-ink-soft">Page {list.page} / {list.pages}</span>
                                <Button variant="outline" size="sm" disabled={page >= list.pages} onClick={() => setPage(page + 1)} data-testid="avis-next">Suivant <ChevronRight size={15} /></Button>
                            </div>
                        )}
                    </>
                )}
            </div>
            <div className="mt-20">
                <SocialProof />
            </div>
            <TestimonialForm
                open={formOpen}
                onClose={() => setFormOpen(false)}
                prefill={federated?.prefill || null}
                reviewToken={federated?.review_token || ""}
            />
        </main>
    );
}
